import {Gear, Link, Star, User} from '@phosphor-icons/react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {Outlet, useLocation, useNavigate} from 'react-router';
import AppPaper from '../../../components/AppPaper';
import {PageContainer} from '../../../components/Layout';

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  matchPath: string;
};

const Component = () => {
  const theme = useTheme();
  const {formatMessage} = useIntl();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navItems: NavItem[] = [
    {
      label: formatMessage(messages.settings.account),
      path: '/app/settings/account',
      icon: <User />,
      matchPath: 'account',
    },
    {
      label: formatMessage(messages.settings.premium),
      path: '/app/settings/premium',
      icon: <Star />,
      matchPath: 'premium',
    },
    {
      label: formatMessage(messages.settings.preferencesSection),
      path: '/app/settings/preferences',
      icon: <Gear />,
      matchPath: 'preferences',
    },
    {
      label: formatMessage(messages.settings.weatherServices),
      path: '/app/settings/services',
      icon: <Link />,
      matchPath: 'services',
    },
  ];

  const isSelected = (item: NavItem) => pathname.includes(item.matchPath);

  // Mobile: horizontal tabs at top
  if (isMobile) {
    return (
      <PageContainer title={formatMessage(messages.settings.settings)}>
        {/* Horizontal tab navigation */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 3,
            overflowX: 'auto',
            pb: 1,
          }}>
          {navItems.map((item) => (
            <Box
              key={item.path}
              onClick={() => navigate(item.path)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                cursor: 'pointer',
                flexShrink: 0,
                backgroundColor: isSelected(item)
                  ? 'primary.main'
                  : 'action.hover',
                color: isSelected(item)
                  ? 'primary.contrastText'
                  : 'text.primary',
                fontWeight: isSelected(item) ? 600 : 400,
                transition: 'all 200ms',
                '&:hover': {
                  backgroundColor: isSelected(item)
                    ? 'primary.dark'
                    : 'action.selected',
                },
              }}>
              {item.icon}
              {item.label}
            </Box>
          ))}
        </Box>

        {/* Content */}
        <Outlet />
      </PageContainer>
    );
  }

  // Desktop: sidebar navigation
  return (
    <PageContainer title={formatMessage(messages.settings.settings)}>
      <Box sx={{display: 'flex', gap: 3}}>
        {/* Sidebar navigation */}
        <AppPaper sx={{width: 240, flexShrink: 0, height: 'fit-content', p: 1}}>
          <List disablePadding>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={isSelected(item)}
                  onClick={() => navigate(item.path)}
                  sx={{borderRadius: 2}}>
                  <ListItemIcon sx={{minWidth: 40}}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: {
                        sx: {fontWeight: isSelected(item) ? 600 : 400},
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </AppPaper>

        {/* Content area */}
        <Box sx={{flex: 1, minWidth: 0}}>
          <Outlet />
        </Box>
      </Box>
    </PageContainer>
  );
};

Component.displayName = 'Settings';

export default Component;
